import type { FC } from 'react';
import cn from 'classnames';
import { Button } from '@selfinspectionorg/ui-kit';
import { MfaMethod } from '@shared/services';
import styles from './Mfa.module.scss';
import { useMfaSetup, type MfaSetupExitReason, type MfaSetupResult } from './useMfaSetup';
import { usePasskeyCeremony } from './usePasskeyCeremony';
import { BackupCodes, EmailStep, MethodPicker, MfaLeaveGuard, PasskeyButton, TotpSetup } from './components';
import { Card } from '@/components/ui';

interface Props {
  mfaPending: string;
  mfaMethods: MfaMethod[];
  /** A finished enrollment (after any backup-code acknowledgement) with its raw confirm result. */
  onComplete: (result: MfaSetupResult) => void;
  /** An unfinished exit; the host decides where it goes (reset the wizard, or leave the page). */
  onExit: (reason: MfaSetupExitReason) => void;
}

/**
 * The MFA enrollment ceremony core, rendered inline by the login wizard on `/login` (its only
 * host). Reuses the shared step components in enroll mode. It never navigates and never adopts a
 * session itself — completion and every exit are handed to the host via `onComplete`/`onExit`.
 */
export const MfaSetup: FC<Props> = ({ mfaPending, mfaMethods, onComplete, onExit }) => {
  const flow = useMfaSetup(mfaMethods, onComplete);
  const passkey = usePasskeyCeremony({
    mfaPending,
    mode: 'enroll',
    onSuccess: flow.completeEnrollment,
    onExpired: flow.expire,
  });

  if (flow.step === 'expired') {
    return (
      <Card className={styles.wrapper}>
        <Card.Title
          title="Session expired"
          description="Your setup session has expired. Please sign in again to set up two-factor authentication."
        />
        <Button width="100%" onClick={() => onExit('expired')} data-testid="mfa-back-to-login-button">
          Back to Login
        </Button>
      </Card>
    );
  }

  if (flow.step === 'backup-codes') {
    return (
      <Card className={styles.wrapper}>
        <MfaLeaveGuard active={!flow.codesAcknowledged} />
        <BackupCodes codes={flow.backupCodes} onAcknowledge={flow.acknowledgeBackupCodes} />
      </Card>
    );
  }

  if (flow.step === 'select') {
    return (
      <Card className={styles.wrapper}>
        <MethodPicker methods={mfaMethods} mode="enroll" onSelect={flow.selectMethod} />
      </Card>
    );
  }

  // step === 'setup': render the chosen method's enrollment component. With multiple methods, Back
  // returns to the picker; when a single method skipped the picker, Back exits the ceremony.
  const shared = {
    mfaPending,
    onExpired: flow.expire,
    onBack: flow.canReturnToPicker ? flow.backToPicker : () => onExit('back'),
  };

  let content = null;

  if (flow.method === MfaMethod.PASSKEY) {
    content = (
      <PasskeyButton
        mode="enroll"
        isRunning={passkey.isRunning}
        error={passkey.error}
        onRun={passkey.run}
        onBack={shared.onBack}
      />
    );
  } else if (flow.method === MfaMethod.EMAIL) {
    content = <EmailStep {...shared} mode="enroll" onSuccess={flow.completeEnrollment} />;
  } else if (flow.method === MfaMethod.TOTP) {
    content = <TotpSetup {...shared} onConfirmed={flow.completeEnrollment} />;
  }

  // The TOTP enrollment screen carries a denser two-section layout (QR + manual key, then code
  // entry), so it gets a wider card; every other step stays at the default width.
  const isTotpSetup = flow.method === MfaMethod.TOTP;

  return <Card className={cn(styles.wrapper, isTotpSetup && styles.wide)}>{content}</Card>;
};
